// Pure sailing physics — no Pixi dependency.
// Angle convention: 0 = north (up, -y screen). Clockwise = positive.

export interface Vec2 {
  x: number;
  y: number;
}

export function normalize(v: Vec2): Vec2 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y);
  if (len < 0.0001) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

export function dot(a: Vec2, b: Vec2): number {
  return a.x * b.x + a.y * b.y;
}

// 90-degree clockwise rotation
export function perpCW(v: Vec2): Vec2 {
  return { x: v.y, y: -v.x };
}

export function scale(v: Vec2, s: number): Vec2 {
  return { x: v.x * s, y: v.y * s };
}

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

// angle 0 = north (-y), increases clockwise
export function angleToDir(radians: number): Vec2 {
  return { x: Math.sin(radians), y: -Math.cos(radians) };
}

export function dirToAngle(dir: Vec2): number {
  return Math.atan2(dir.x, -dir.y);
}

export interface SailingInput {
  windDir: Vec2; // unit vector: direction wind blows TO
  boatDir: Vec2; // unit vector: boat heading
  sailDir: Vec2; // unit vector: boom/sail direction (world space)
}

export interface SailingResult {
  thrust: number;   // 0..MAX_SPEED
  sailNormal: Vec2; // perpendicular to sail, facing wind side (for rendering)
}

export const MAX_SPEED = 4; // world-units per frame at 60 fps
const DRAG = 0.985;

export function computeSailing(input: SailingInput): SailingResult {
  const { windDir, boatDir, sailDir } = input;

  // Sail normal — pick the side that faces downwind (positive dot with windDir)
  let normal = perpCW(sailDir);
  if (dot(windDir, normal) < 0) {
    normal = { x: -normal.x, y: -normal.y };
  }

  // Angle of attack between wind and sail surface
  const cosAoA = Math.abs(dot(windDir, sailDir));
  const aoa = Math.acos(Math.min(1, cosAoA));
  // aoa 0 = sail parallel to wind (no force), π/2 = perpendicular (max drag, no lift)

  // Lift: perpendicular to wind, toward leeward side. Peaks at 45° AoA.
  const liftCoeff = Math.sin(2 * aoa);
  let liftDir = perpCW(windDir);
  if (dot(liftDir, normal) < 0) {
    liftDir = { x: -liftDir.x, y: -liftDir.y };
  }

  // Drag: along wind direction. Peaks at 90° AoA.
  const dragCoeff = Math.sin(aoa) * Math.sin(aoa);

  // No-go zone: can't sail within ~35° of wind source
  const cosFromSource = -dot(boatDir, windDir);
  const NO_GO_COS = Math.cos(35 * Math.PI / 180); // ~0.819
  if (cosFromSource > NO_GO_COS) {
    return { thrust: 0, sailNormal: normal };
  }

  // Boom must be on the leeward side (the side wind pushes toward).
  // dot(sailDir, windDir) < 0 means boom extends toward the wind source — impossible.
  if (dot(sailDir, windDir) < 0) {
    return { thrust: 0, sailNormal: normal };
  }

  // Combined aerodynamic force (drag scaled down — lift dominates in real sailing)
  const force = add(scale(liftDir, liftCoeff), scale(windDir, dragCoeff * 0.5));

  const thrust = Math.max(0, dot(force, boatDir)) * MAX_SPEED;

  return { thrust, sailNormal: normal };
}

export function updateSpeed(currentSpeed: number, thrust: number): number {
  const dragged = currentSpeed * DRAG;
  const target  = Math.min(thrust, MAX_SPEED);
  // Gradual acceleration: blend 8% toward target each frame
  return Math.max(0, dragged + (target - dragged) * 0.08);
}

export function moveBoat(pos: Vec2, boatDir: Vec2, speed: number): Vec2 {
  return {
    x: pos.x + boatDir.x * speed,
    y: pos.y + boatDir.y * speed,
  };
}
